import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Spinner,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { FaCogs, FaExclamationTriangle } from "react-icons/fa";
import AdvancedAnalysisPanel from "../../../Components/Reports/AdvancedAnalysisPanel";
import {
  getReportComponent,
  getReportDisplayName,
  getReportIcon,
} from "../reportRegistry";
import { ReportPickerMenu } from "./ReportPickerMenu";

const getTabButtonStyles = (isActive) => ({
  bg: isActive ? "linear-gradient(135deg, #7c3aed 0%, #06b6d4 100%)" : "white",
  color: isActive ? "white" : "#1A202C",
  border: "1px solid",
  borderColor: isActive ? "#7c3aed" : "#E2E8F0",
  borderRadius: "md",
  px: 2,
  py: 1,
  fontWeight: "600",
  transition: "all 0.2s ease",
  boxShadow: isActive
    ? "0 2px 8px rgba(124, 58, 237, 0.3)"
    : "0 1px 4px rgba(0, 0, 0, 0.1)",
  fontSize: "14px",
  minHeight: "20px",
  _hover: {
    bg: isActive
      ? "linear-gradient(135deg, #6d28d9 0%, #059bb4 100%)"
      : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    transform: "translateY(-1px)",
  },
});

const renderAlert = (status, description, icon) => (
  <Alert status={status} borderRadius="md" mt={4}>
    <AlertIcon as={icon} />
    <AlertDescription>{description}</AlertDescription>
  </Alert>
);

export const ReportsContent = ({
  activeReport,
  activeReportType,
  error,
  getErrorMessage,
  loading,
  reportsData,
  reportsTexts,
  setTabIndex,
  tabIndex,
  texts,
}) => {
  const renderReportComponent = (report) => {
    if (!report) {
      return renderAlert(
        "info",
        texts.en === "ar"
          ? "لا توجد بيانات متاحة لهذا التقرير"
          : "No data available for this report",
      );
    }

    const Component = getReportComponent(report.ReportKey);
    if (!Component) {
      return renderAlert(
        "warning",
        texts.en === "ar"
          ? `مكون التقرير غير متوفر: ${report.ReportKey}`
          : `Report component not available: ${report.ReportKey}`,
      );
    }

    return <Component data={report} />;
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="400px">
        <Spinner size="xl" color="purple.500" />
      </Flex>
    );
  }

  if (error) {
    return renderAlert("error", getErrorMessage(), FaExclamationTriangle);
  }

  const financialReports = reportsData.financialStatements.map(
    (report, index) => ({
      icon: getReportIcon(report.ReportKey),
      index,
      isSelected: tabIndex === index,
      key: report.ReportKey,
      label: getReportDisplayName(report.ReportKey, reportsTexts, texts.en),
    }),
  );
  const ratioReports = reportsData.financialRatios.map((report, index) => {
    const absoluteIndex = reportsData.financialStatements.length + index;
    return {
      icon: getReportIcon(report.ReportKey),
      index: absoluteIndex,
      isSelected: tabIndex === absoluteIndex,
      key: report.ReportKey,
      label: getReportDisplayName(report.ReportKey, reportsTexts, texts.en),
    };
  });
  const advancedTabIndex =
    reportsData.financialStatements.length + reportsData.financialRatios.length;

  return (
    <Tabs variant="unstyled" index={tabIndex} onChange={setTabIndex} h={"100%"}>
      <TabList m={0} gap={1} flexWrap="wrap" alignItems="center" h={"2.2rem"}>
        {financialReports.length > 0 && (
          <ReportPickerMenu
            activeIcon={
              activeReportType === "financial"
                ? getReportIcon(activeReport?.ReportKey)
                : getReportIcon("Wafeq_trial_balance")
            }
            activeLabel={
              activeReportType === "financial"
                ? getReportDisplayName(
                    activeReport?.ReportKey,
                    reportsTexts,
                    texts.en,
                  )
                : null
            }
            defaultLabel={
              texts.en === "Arabic"
                ? reportsTexts.financialStatementsAr || "السجلات المالية"
                : reportsTexts.financialStatements || "Financial Statements"
            }
            isActive={activeReportType === "financial"}
            items={financialReports}
            onSelect={setTabIndex}
          />
        )}
        {ratioReports.length > 0 && (
          <ReportPickerMenu
            activeIcon={
              activeReportType === "ratios"
                ? getReportIcon(activeReport?.ReportKey)
                : getReportIcon("Wafeq_structural_ratios")
            }
            activeLabel={
              activeReportType === "ratios"
                ? getReportDisplayName(
                    activeReport?.ReportKey,
                    reportsTexts,
                    texts.en,
                  )
                : null
            }
            defaultLabel={
              texts.en === "Arabic"
                ? reportsTexts.financialRatiosAr || "النسب المالية"
                : reportsTexts.financialRatios || "Financial Ratios"
            }
            isActive={activeReportType === "ratios"}
            items={ratioReports}
            onSelect={setTabIndex}
          />
        )}
        <Button
          onClick={() => setTabIndex(advancedTabIndex)}
          sx={getTabButtonStyles(tabIndex === advancedTabIndex)}
          size={"sm"}
        >
          <HStack spacing={2}>
            <Icon as={FaCogs} boxSize="14px" />
            <Text fontSize="sm" fontWeight="600" noOfLines={1}>
              {texts.en === "Arabic"
                ? reportsTexts.advancedAnalysisAr || "التحليل المتقدم"
                : reportsTexts.advancedAnalysis || "Advanced Analysis"}
            </Text>
          </HStack>
        </Button>
      </TabList>
      <TabPanels maxH="calc(100dvh - 5rem)" overflowY={"auto"}>
        {reportsData.financialStatements.map((report, index) => (
          <TabPanel key={report.ReportKey} p={0}>
            {tabIndex === index && renderReportComponent(report)}
          </TabPanel>
        ))}

        {reportsData.financialRatios.map((report, index) => {
          const absoluteIndex = reportsData.financialStatements.length + index;
          return (
            <TabPanel key={report.ReportKey} p={0}>
              {tabIndex === absoluteIndex && renderReportComponent(report)}
            </TabPanel>
          );
        })}

        <TabPanel p={0}>
          {tabIndex === advancedTabIndex && (
            <Box>
              <AdvancedAnalysisPanel
                aiAnalysisReports={reportsData.aiAnalysis}
              />
            </Box>
          )}
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};
